// components/CapsulesList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Copy, 
  Settings
} from 'lucide-react';
import { dbService } from '../lib/supabase';

const CapsulesList = () => {
  const [capsules, setCapsules] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCapsule, setEditingCapsule] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    client_id: '', 
    password: '',
    is_published: false 
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCapsules();
    loadClients();
  }, []);

  const loadCapsules = async () => {
    setLoading(true);
    const { data, error } = await dbService.getCapsules();
    if (!error) setCapsules(data || []);
    setLoading(false);
  };

  const loadClients = async () => {
    const { data, error } = await dbService.getClients();
    if (!error) setClients(data || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let result;
    
    if (editingCapsule) {
      result = await dbService.updateCapsule(editingCapsule.id, formData);
    } else {
      result = await dbService.createCapsule(formData);
    }
    
    if (!result.error) {
      loadCapsules();
      resetForm();
    }
  };

  const handleEdit = (e, capsule) => {
    e.stopPropagation(); // Prevent card click
    setEditingCapsule(capsule);
    setFormData({
      name: capsule.name,
      description: capsule.description || '',
      client_id: capsule.client_id,
      password: capsule.password || '',
      is_published: capsule.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // Prevent card click
    if (window.confirm('Are you sure you want to delete this capsule?')) {
      await dbService.deleteCapsule(id);
      loadCapsules();
    }
  };

  const copyLink = (e, id) => {
    e.stopPropagation(); // Prevent card click
    const url = `${window.location.origin}/capsule/${id}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', client_id: '', password: '', is_published: false });
    setEditingCapsule(null);
    setShowForm(false);
  };

  const handleCapsuleClick = (capsuleId) => {
    navigate(`/dashboard/capsules/${capsuleId}`);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-light text-gray-900">Capsules</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" />
          <span>New Capsule</span>
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-medium mb-4">
              {editingCapsule ? 'Edit Capsule' : 'Create New Capsule'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                required
              >
                <option value="">Select Client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Capsule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                required
              />
              <textarea
                placeholder="Description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                rows="3"
              />
              <input
                type="text"
                placeholder="Password for client access"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              />
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Published (visible to client)</span>
              </label>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800"
                >
                  {editingCapsule ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {capsules.map((capsule) => (
          <div
            key={capsule.id}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleCapsuleClick(capsule.id)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">{capsule.name}</h3>
                <p className="text-gray-600">{capsule.clients?.name}</p>
                {capsule.description && (
                  <p className="text-gray-500 mt-2">{capsule.description}</p>
                )}
                <div className="flex items-center space-x-4 mt-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    capsule.is_published 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {capsule.is_published ? 'Published' : 'Draft'}
                  </span>
                  {capsule.password && (
                    <span className="text-xs text-gray-500">Password Protected</span>
                  )}
                  <span className="text-xs text-gray-500">
                    {capsule.items?.length || 0} items • {capsule.outfits?.length || 0} outfits
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => window.open(`/capsule/${capsule.id}`, '_blank')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Preview"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => copyLink(e, capsule.id)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Copy Link"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => handleEdit(e, capsule)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Edit Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => handleDelete(e, capsule.id)}
                  className="p-2 text-gray-400 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CapsulesList;